import { useMemo, useState } from "react";

import PropTypes from "prop-types";

import { Alert, Slide, Snackbar } from "@mui/material";

import SnackbarContext from "./snackbar-context";

// A snackbar can only render text. Callers occasionally hand it whatever they
// have (an array of validation sentences, a field object); rendering that
// directly makes React throw and blanks the app, so flatten it here.
const toDisplayMessage = (message) => {
  if (typeof message === "string") return message;
  if (message == null) return "";
  if (Array.isArray(message)) {
    return message.map(toDisplayMessage).filter(Boolean).join(" ");
  }
  if (typeof message === "object") {
    return toDisplayMessage(Object.values(message));
  }
  return String(message);
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    message: null,
    severity: "error",
  });

  const showSnackbar = useMemo(
    () =>
      (message, severity = "error") => {
        setSnackbar({ message: toDisplayMessage(message), severity });
      },
    []
  );

  const closeSnackbar = (_, reason) => {
    // Avoid closing when user clicks outside the Snackbar
    if (reason === "clickaway") {
      return;
    }

    setSnackbar({ message: null, severity: "error" });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {!!snackbar.message && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          // Errors stay until dismissed: they are often long, name documents
          // the user has to act on, and answer a submit the user is waiting
          // for. Confirmations still get out of the way on their own.
          autoHideDuration={snackbar.severity === "error" ? null : 5000}
          onClose={closeSnackbar}
          TransitionComponent={(props) => <Slide {...props} direction="left" />}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
